import React, { useEffect, useState } from "react";
import api from "../../api/axiosConfig";
import format from "date-fns/format";
import parse from "date-fns/parse";
import {
  MdKeyboardDoubleArrowLeft,
  MdKeyboardDoubleArrowRight,
} from "react-icons/md";
import { useAuth } from "../Authentication/AuthContext";
import socket from "../../socket";

const CalendarChart = () => {
  const [calendarData, setCalendarData] = useState([]);
  const [error, setError] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const { userDetails } = useAuth();
  const roleName = userDetails.role_name;
  const userId = userDetails.id;
  const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const fetchCalendarData = async (month, year) => {
    try {
      const res = await api.get(`/api/timesheet/dailyduration`, {
        params: { month: month + 1, year },
      });

      const formattedData = res.data.map((entry) => {
        const dayDate = new Date(entry.day);
        return {
          ...entry,
          day: format(dayDate, "dd-MM-yyyy"),
        };
      });

      setCalendarData(formattedData);
    } catch (error) {
      console.error(
        "Error fetching calendar data:",
        error.response?.data || error.message
      );
      setError("Failed to load calendar data.");
    }
  };

  useEffect(() => {
    fetchCalendarData(currentMonth, currentYear);
  }, [currentMonth, currentYear]);

  useEffect(() => {
    socket.on("connect", () => {
      console.log("Connected to WebSocket server overview");
    });

    socket.on("timesheet added", () => {
      fetchCalendarData(currentMonth, currentYear);
    });

    socket.on("timesheet updated", () => {
      fetchCalendarData(currentMonth, currentYear);
    });

    socket.on("timesheet bulk update", () => {
      fetchCalendarData(currentMonth, currentYear);
    });
    socket.on("timesheet deleted", ({ ids }) => {
      fetchCalendarData(currentMonth, currentYear);
    });

    return () => {
      socket.off("timesheet deleted"); // Cleanup
      socket.off("timesheet updated");
      socket.off("timesheet bulk update");
      socket.off("timesheet added");
    };
  }, [socket, userDetails, userId]);

  useEffect(() => {
    fetchCalendarData(currentMonth, currentYear);
  }, [currentMonth, currentYear]);
  const getTotalBookedHoursForDay = (day) => {
    const currentDate = new Date(currentYear, currentMonth, day + 1);

    let totalProductiveMinutes = 0;
    let totalNonProductiveMinutes = 0;

    calendarData.forEach((entry) => {
      const entryDate = parse(entry.day, "dd-MM-yyyy", new Date());

      if (
        entryDate.getTime() === currentDate.getTime() &&
        (roleName === "Administrator" ||
          roleName === "Super Admin" ||
          entry.user_id === userId)
      ) {
        const [prodHours, prodMinutes] = entry.productive_hours
          .split(":")
          .map(Number);
        const [nonProdHours, nonProdMinutes] = entry.non_productive_hours
          .split(":")
          .map(Number);

        totalProductiveMinutes += prodHours * 60 + prodMinutes;
        totalNonProductiveMinutes += nonProdHours * 60 + nonProdMinutes;
      }
    });

    const formattedProductive = `${String(
      Math.floor(totalProductiveMinutes / 60)
    ).padStart(2, "0")}:${String(totalProductiveMinutes % 60).padStart(
      2,
      "0"
    )}`;

    const formattedNonProductive = `${String(
      Math.floor(totalNonProductiveMinutes / 60)
    ).padStart(2, "0")}:${String(totalNonProductiveMinutes % 60).padStart(
      2,
      "0"
    )}`;

    return { formattedProductive, formattedNonProductive };
  };

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const firstDayIndex = new Date(currentYear, currentMonth, 1).getDay();
  const currentMonthDays = getDaysInMonth(currentMonth, currentYear);
  const totalGridSlots = 42;
  const remainingSlots = totalGridSlots - (firstDayIndex + currentMonthDays);

  return (
    <div>
      <div className="flex items-center justify-end -mt-7 md:-mt-4 lg:-mt-2">
        <button
          className="calendar-month-arrow"
          onClick={handlePrevMonth}
          style={{ border: "none", cursor: "pointer", color: "#6200ee" }}
        >
          <MdKeyboardDoubleArrowLeft style={{ verticalAlign: "middle" }} />
        </button>
        <p
          style={{ fontSize: "16px", color: "#6200ee" }}
        >{`${monthNames[currentMonth]} ${currentYear}`}</p>
        <button
          className="calendar-month-arrow"
          onClick={handleNextMonth}
          style={{ border: "none", cursor: "pointer", color: "#6200ee" }}
        >
          <MdKeyboardDoubleArrowRight style={{ verticalAlign: "middle" }} />
        </button>
      </div>
      <div className="grid grid-cols-7 border-b border-gray-300 border-[0.5px] border-solid border-t-0 border-x-0">
        {dayNames.map((day) => (
          <div
            className="font-bold text-sm pb-[10px] "
            style={{
              borderBottom: "2px solid #6200ee",
              cursor: "pointer",
              color: "#6200ee",
            }}
            key={day}
          >
            {day}
          </div>
        ))}

        {Array.from({ length: firstDayIndex }).map((_, index) => {
          const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
          const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
          const daysInPrevMonth = getDaysInMonth(prevMonth, prevYear);
          const dayNumber = daysInPrevMonth - firstDayIndex + index + 1;

          return (
            <div
              key={`prev-${index}`}
              className="calendar-day calendar-prev-month"
              style={{ color: "#ccc", textAlign: "right" }}
            >
              {dayNumber}
            </div>
          );
        })}

        {[...Array(currentMonthDays).keys()].map((day) => {
          const { formattedProductive, formattedNonProductive } =
            getTotalBookedHoursForDay(day);
          const dayDate = new Date(currentYear, currentMonth, day + 1);
          const isToday =
            dayDate.getDate() === new Date().getDate() &&
            dayDate.getMonth() === new Date().getMonth() &&
            dayDate.getFullYear() === new Date().getFullYear();

          let saturdayCount = 0;
          for (let i = 1; i <= day + 1; i++) {
            const tempDate = new Date(currentYear, currentMonth, i);
            if (tempDate.getDay() === 6) {
              saturdayCount++;
            }
          }

          const isHolidaySaturday =
            dayDate.getDay() === 6 &&
            (saturdayCount === 1 || saturdayCount === 3 || saturdayCount === 5);

          return (
            <div
              className={`calendar-day ${isToday ? "calendar-today" : ""}`}
              key={day}
              style={{
                backgroundColor: "transparent",
                fontWeight: "400",
                color: isHolidaySaturday
                  ? "red"
                  : dayDate.getDay() === 0
                  ? "red"
                  : "black",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  width: isToday ? "30px" : "auto",
                  height: isToday ? "20px" : "auto",
                  borderRadius: isToday ? "50%" : "0",
                  backgroundColor: isToday ? "#7ecafc" : "transparent",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: isToday ? "1px" : "0",
                  top: isToday ? "-1px" : "0",
                  right:
                    window.innerWidth >= 1440 && window.innerWidth <= 1919
                      ? isToday
                        ? "-2.5rem"
                        : "-1.7rem"
                      : isToday
                      ? "-4rem"
                      : "-2.5rem",
                }}
              >
                {day + 1}
              </div>
              <div
                style={{ display: "flex", flexDirection: "row", gap: "20px" }}
              >
                <div
                  style={{
                    color: "#007f5f",
                    fontSize: "14px",
                    right:
                      window.innerWidth >= 1440 && window.innerWidth <= 1919
                        ? isToday
                          ? "2.9rem"
                          : "2.9rem"
                        : isToday
                        ? "2rem"
                        : "2rem",
                    top: isToday ? "0.5rem" : "0.7rem",
                  }}
                >
                  {formattedProductive === "00:00" ? "" : formattedProductive}
                </div>
                <div
                  style={{
                    color: "#a52a2a",
                    fontSize: "14px",
                    right:
                      window.innerWidth >= 1440 && window.innerWidth <= 1919
                        ? isToday
                          ? "3.4rem"
                          : "3.5rem"
                        : isToday
                        ? "2rem"
                        : "2rem",
                    top: isToday ? "0.5rem" : "0.7rem",
                  }}
                >
                  {formattedNonProductive === "00:00"
                    ? ""
                    : formattedNonProductive}
                </div>
              </div>
            </div>
          );
        })}

        {Array.from({ length: remainingSlots }).map((_, index) => (
          <div
            key={`next-${index}`}
            className="calendar-day calendar-next-month"
            style={{ color: "#ccc", textAlign: "right" }}
          >
            {index + 1}
          </div>
        ))}
      </div>
      <div
        style={{
          display: "flex",
          gap: "20px",
          position: "absolute",
          right: "2rem",
        }}
      >
        <p
          style={{
            color: "#007f5f",
          }}
        >
          Productive
        </p>
        <p
          style={{
            color: "#a52a2a",
          }}
        >
          Non Productive
        </p>
      </div>
      {error && <div className="error-message">{error}</div>}
    </div>
  );
};

export default CalendarChart;
